// Service de gestion des images avec gestion d'erreurs robuste
import axios from 'axios';
import ErrorHandler from './errorHandler.js';

const API_BASE_URL = process.env.REACT_APP_API_URL || (typeof window !== 'undefined' ? window.location.origin : 'http://localhost:3001');
const RUNTIME_BASE = (typeof window !== 'undefined' && window.location && window.location.origin) ? window.location.origin : 'http://localhost:3001';
const EFFECTIVE_API_BASE = (process.env.REACT_APP_API_URL && !/localhost/i.test(process.env.REACT_APP_API_URL)) ? process.env.REACT_APP_API_URL : API_BASE_URL.includes('localhost') ? RUNTIME_BASE : API_BASE_URL;

// Constantes pour le cache d'images
const IMAGE_CACHE_KEY = 'image_cache';
const MAX_CACHE_SIZE = 50; // Nombre max d'images en cache
const MAX_CACHE_AGE = 7 * 24 * 60 * 60 * 1000; // 7 jours en millisecondes

class ImageService {
  static STORAGE_KEY = 'image_cache';
  static PENDING_KEY = 'image_uploads_pending';
  static MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB (conforme tests)
  static ALLOWED_TYPES = ['image/jpeg', 'image/png', 'image/webp'];

  static validateImage(file) {
    const errors = [];

    if (!file) {
      errors.push('Aucun fichier sélectionné');
      return errors;
    }

    if (file.size > this.MAX_FILE_SIZE) {
      errors.push("L'image ne doit pas dépasser 5Mo");
    }

    if (!file.type || !/^image\//.test(file.type)) {
      errors.push('Le fichier doit être une image');
    }

    return errors;
  }

  static async uploadImage(file, onProgress = null) {
    try {
      // Validation du fichier
      const validationErrors = this.validateImage(file);
      if (validationErrors.length > 0) {
        const validationError = new Error(validationErrors.join(', '));
        ErrorHandler.logValidationError(validationError, 'Image Upload Validation', {
          fileName: file.name,
          fileSize: file.size,
          fileType: file.type,
          errors: validationErrors,
        });
        throw validationError;
      }

      console.log("📤 Upload d'image en cours:", file.name);

      const formData = new FormData();
      formData.append('image', file);

      const res = await axios.post(`${API_BASE_URL}/api/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 60000,
        onUploadProgress: (evt) => {
          if (!onProgress || !evt.total) return;
          const pct = (evt.loaded / evt.total) * 100;
          onProgress(pct);
        },
      });

      const data = res.data || {};
      const imageUrl = data.imageUrl || data.url;
      if (imageUrl) {
        this.cacheImageUrl(file.name, imageUrl);
        console.log('✅ Image uploadée:', imageUrl);
        return imageUrl;
      }
      // fallback: retourner data brut si pas d'URL claire
      return data;
    } catch (error) {
      ErrorHandler.logError(error, 'Image Upload Process', ErrorHandler.ERROR_TYPES.API, {
        fileName: file?.name,
        fileSize: file?.size,
        fileType: file?.type,
      });
      throw error;
    }
  }

  static async deleteImage(imageUrl) {
    try {
      console.log("🗑️ Suppression d'image:", imageUrl);

      // Extraire le nom du fichier de l'URL
      const fileName = imageUrl.split('/').pop();

      const response = await fetch(`${API_BASE_URL}/api/images/${fileName}`, {
        method: 'DELETE',
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      console.log('✅ Image supprimée avec succès');

      // Retirer du cache
      this.removeCachedImage(fileName);

      return { success: true };
    } catch (error) {
      ErrorHandler.logApiError(error, 'Delete Image', {
        imageUrl,
        operation: 'delete',
      });
      throw error;
    }
  }

  static optimizeImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
    return new Promise((resolve, reject) => {
      try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        const img = new Image();

        img.onload = () => {
          try {
            // Calculer les nouvelles dimensions
            let { width, height } = img;

            if (width > maxWidth) {
              height = (height * maxWidth) / width;
              width = maxWidth;
            }

            if (height > maxHeight) {
              width = (width * maxHeight) / height;
              height = maxHeight;
            }

            canvas.width = width;
            canvas.height = height;

            // Dessiner l'image redimensionnée
            ctx.drawImage(img, 0, 0, width, height);

            // Convertir en blob
            canvas.toBlob(
              (blob) => {
                if (blob) {
                  const optimizedFile = new File([blob], file.name, {
                    type: file.type,
                    lastModified: Date.now(),
                  });

                  console.log(`✨ Image optimisée: ${file.size} → ${blob.size} bytes`);
                  resolve(optimizedFile);
                } else {
                  throw new Error('Impossible de créer le blob optimisé');
                }
              },
              file.type,
              quality
            );
          } catch (error) {
            ErrorHandler.logError(error, 'Image Optimization Canvas', ErrorHandler.ERROR_TYPES.UI, {
              fileName: file.name,
              originalSize: file.size,
              targetDimensions: `${maxWidth}x${maxHeight}`,
            });
            reject(error);
          }
        };

        img.onerror = () => {
          const loadError = new Error("Impossible de charger l'image pour optimisation");
          ErrorHandler.logError(loadError, 'Image Optimization Load', ErrorHandler.ERROR_TYPES.UI, {
            fileName: file.name,
            fileType: file.type,
          });
          reject(loadError);
        };

        img.src = URL.createObjectURL(file);
      } catch (error) {
        ErrorHandler.logError(error, 'Image Optimization Setup', ErrorHandler.ERROR_TYPES.UI, {
          fileName: file.name,
        });
        reject(error);
      }
    });
  }
  // Alias de compatibilité avec les tests
  static async compressImage(file, maxWidth = 1920, maxHeight = 1080, quality = 0.8) {
    return this.optimizeImage(file, maxWidth, maxHeight, quality);
  }

  static cacheImageUrl(fileName, url) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      cache[fileName] = {
        url,
        timestamp: Date.now(),
      };
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache));
    } catch (error) {
      ErrorHandler.logError(error, 'Cache Image URL', ErrorHandler.ERROR_TYPES.UI, {
        fileName,
        url,
      });
    }
  }

  static getCachedImageUrl(fileName) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      const cached = cache[fileName];

      if (cached && Date.now() - cached.timestamp < 24 * 60 * 60 * 1000) {
        // 24h
        return cached.url;
      }

      return null;
    } catch (error) {
      ErrorHandler.logError(error, 'Get Cached Image URL', ErrorHandler.ERROR_TYPES.UI, {
        fileName,
      });
      return null;
    }
  }

  static removeCachedImage(fileName) {
    try {
      const cache = JSON.parse(localStorage.getItem(this.STORAGE_KEY) || '{}');
      delete cache[fileName];
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(cache));
    } catch (error) {
      ErrorHandler.logError(error, 'Remove Cached Image', ErrorHandler.ERROR_TYPES.UI, {
        fileName,
      });
    }
  }

  static addToPendingQueue(file, optimized = false) {
    try {
      const pending = JSON.parse(localStorage.getItem(this.PENDING_KEY) || '[]');
      pending.push({
        fileName: file.name,
        fileSize: file.size,
        fileType: file.type,
        optimized,
        timestamp: Date.now(),
        id: `pending_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
      });
      localStorage.setItem(this.PENDING_KEY, JSON.stringify(pending));
    } catch (error) {
      ErrorHandler.logError(error, 'Add Image to Pending Queue', ErrorHandler.ERROR_TYPES.UI, {
        fileName: file.name,
      });
    }
  }

  static clearPendingQueue() {
    try {
      localStorage.removeItem(this.PENDING_KEY);
    } catch (error) {
      ErrorHandler.logError(error, 'Clear Image Pending Queue', ErrorHandler.ERROR_TYPES.UI);
    }
  }
}

// Export de la fonction uploadImage pour compatibilité
export const uploadImage = ImageService.uploadImage.bind(ImageService);

export default ImageService;

// Résout une URL d'image potentiellement relative vers une URL absolue pointant vers l'API
export function resolveImageUrl(url) {
  try {
    if (!url || typeof url !== 'string') return url;
    if (/^data:/i.test(url)) return url; // data URI
    // Si déjà absolue
    if (/^https?:\/\//i.test(url)) return url;
    const base = EFFECTIVE_API_BASE.replace(/\/$/, '');
    if (url.startsWith('/uploads/')) return `${base}${url}`;
    if (url.startsWith('uploads/')) return `${base}/${url}`;
    return new URL(url, `${base}/`).toString();
  } catch (_) {
    return url;
  }
}

// Fonction pour nettoyer les entrées textuelles (prévention XSS)
export function sanitizeInput(str) {
  if (typeof str !== 'string') return str;
  return str
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#039;');
}

// Système de cache d'images
export function addToImageCache(key, imageUrl) {
  try {
    // Récupérer le cache actuel
    const cache = getImageCache();

    // Vérifier si l'élément existe déjà et le mettre à jour
    const existingIndex = cache.findIndex((item) => item.key === key);
    const timestamp = Date.now();

    if (existingIndex >= 0) {
      cache[existingIndex] = { key, imageUrl, timestamp };
    } else {
      // Ajouter le nouvel élément
      cache.push({ key, imageUrl, timestamp });
    }

    // Nettoyer le cache si nécessaire
    const cleanedCache = cleanImageCache(cache);

    // Sauvegarder le cache
    localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cleanedCache));
  } catch (error) {
    console.error("Erreur lors de l'ajout au cache:", error);
  }
}

export function getFromImageCache(key) {
  try {
    const cache = getImageCache();
    const item = cache.find((item) => item.key === key);

    if (item) {
      // Vérifier si l'élément n'est pas expiré
      if (Date.now() - item.timestamp < MAX_CACHE_AGE) {
        return item.imageUrl;
      } else {
        // Supprimer l'élément expiré
        removeFromImageCache(key);
        return null;
      }
    }

    return null;
  } catch (error) {
    console.error('Erreur lors de la récupération du cache:', error);
    return null;
  }
}

function getImageCache() {
  try {
    const cacheData = localStorage.getItem(IMAGE_CACHE_KEY);
    return cacheData ? JSON.parse(cacheData) : [];
  } catch (error) {
    console.error('Erreur lors de la lecture du cache:', error);
    return [];
  }
}

function removeFromImageCache(key) {
  try {
    let cache = getImageCache();
    cache = cache.filter((item) => item.key !== key);
    localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify(cache));
  } catch (error) {
    console.error('Erreur lors de la suppression du cache:', error);
  }
}

function cleanImageCache(cache) {
  // Supprimer les éléments expirés
  let cleanedCache = cache.filter((item) => Date.now() - item.timestamp < MAX_CACHE_AGE);

  // Si le cache est toujours trop grand, supprimer les plus anciens
  if (cleanedCache.length > MAX_CACHE_SIZE) {
    cleanedCache.sort((a, b) => b.timestamp - a.timestamp); // Plus récents en premier
    cleanedCache = cleanedCache.slice(0, MAX_CACHE_SIZE);
  }

  return cleanedCache;
}

// Préchargement d'images pour améliorer les performances
export function preloadImages(urls) {
  urls.forEach((url) => {
    const img = new Image();
    img.src = url;
  });
}

// Exports de compatibilité pour les tests
export const compressImage = ImageService.compressImage.bind(ImageService);

// Retourne l'URL d'une variante WebP (`-w{width}.webp`) pour une image d'upload,
// ou null si non dérivable. ⚠️ Toutes les largeurs n'existent pas forcément sur
// le disque (les originaux plus petits que `width` ne sont pas agrandis) : l'appelant
// DOIT prévoir un fallback `onError` vers l'image d'origine.
export function webpVariantUrl(imageUrl, width = 480) {
  if (typeof imageUrl !== 'string') return null;
  try {
    const absoluteUrl = resolveImageUrl(imageUrl);
    const url = new URL(absoluteUrl);
    const filename = url.pathname.split('/').pop() || '';
    const dot = filename.lastIndexOf('.');
    if (dot <= 0) return null;
    const base = filename.substring(0, dot);
    const root = absoluteUrl.replace(filename, '');
    return `${root}${base}-w${width}.webp`;
  } catch (_) {
    return null;
  }
}

// Helper: construit des srcset WebP + fallback depuis une URL d'upload
export function buildResponsiveSources(imageUrl) {
  if (typeof imageUrl !== 'string') return {};
  try {
    // Utiliser la base API pour résoudre les chemins relatifs
    const absoluteUrl = resolveImageUrl(imageUrl);
    const url = new URL(absoluteUrl);
    const filename = url.pathname.split('/').pop() || '';
    const dot = filename.lastIndexOf('.');
    if (dot <= 0) return {};
    const ext = filename.substring(dot).toLowerCase();
    const base = filename.substring(0, dot);
    const widths = [320, 480, 768, 1024, 1440];
    const root = absoluteUrl.replace(filename, '');
    const isPng = ext === '.png';
    const isJpg = ext === '.jpg' || ext === '.jpeg';

    const webpSrcset = widths.map((w) => `${root}${base}-w${w}.webp ${w}w`).join(', ');
    let fallbackSrcset = '';
    let typeFallback;
    if (isPng || isJpg) {
      const fext = isPng ? '.png' : '.jpg';
      fallbackSrcset = widths.map((w) => `${root}${base}-w${w}${fext} ${w}w`).join(', ');
      typeFallback = isPng ? 'image/png' : 'image/jpeg';
    } else if (ext === '.webp') {
      // original webp → pas de fallback connu
      typeFallback = 'image/webp';
    }
    return { webpSrcset, fallbackSrcset, typeFallback };
  } catch (_) {
    return {};
  }
}
